import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../services/api";
import { GET_TUTORIALS } from "../../services/urls";

const emptyBrowse = {
  currentFolderId: null,
  breadcrumbs: [],
  folders: [],
  files: [],
};

const initialState = {
  loading: false,
  tutorials: [],
  tutorialDetails: null,
  browse: emptyBrowse,
  error: null,
};

const extractData = (response) => {
  if (response.data?.status && response.data?.data !== undefined) {
    return response.data.data;
  }
  return response.data;
};

export const fetchTutorialBrowse = createAsyncThunk(
  "tutorials/fetchTutorialBrowse",
  async (folderId, { rejectWithValue }) => {
    try {
      const params = folderId ? { folderId } : {};
      const response = await api.get(`${GET_TUTORIALS}/browse`, { params });
      return extractData(response) || emptyBrowse;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to browse tutorials",
      );
    }
  },
);

export const fetchTutorials = createAsyncThunk(
  "tutorials/fetchTutorials",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get(GET_TUTORIALS);
      return extractData(response) || [];
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch tutorials",
      );
    }
  },
);

export const fetchTutorialDetails = createAsyncThunk(
  "tutorials/fetchTutorialDetails",
  async (tutorialId, { rejectWithValue }) => {
    try {
      const response = await api.get(`${GET_TUTORIALS}/${tutorialId}`);
      return extractData(response);
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch tutorial details",
      );
    }
  },
);

export const createTutorialFolder = createAsyncThunk(
  "tutorials/createTutorialFolder",
  async ({ name, parentId }, { rejectWithValue }) => {
    try {
      const response = await api.post(`${GET_TUTORIALS}/folders`, {
        name,
        parentId: parentId || null,
      });
      return extractData(response);
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to create folder",
      );
    }
  },
);

export const updateTutorialFolder = createAsyncThunk(
  "tutorials/updateTutorialFolder",
  async ({ folderId, name, parentId }, { rejectWithValue }) => {
    try {
      const body = {};
      if (name !== undefined) body.name = name;
      if (parentId !== undefined) body.parentId = parentId;
      const response = await api.put(
        `${GET_TUTORIALS}/folders/${folderId}`,
        body,
      );
      return extractData(response);
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update folder",
      );
    }
  },
);

export const deleteTutorialFolder = createAsyncThunk(
  "tutorials/deleteTutorialFolder",
  async (folderId, { rejectWithValue }) => {
    try {
      await api.delete(`${GET_TUTORIALS}/folders/${folderId}`);
      return folderId;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete folder",
      );
    }
  },
);

export const createTutorial = createAsyncThunk(
  "tutorials/createTutorial",
  async (tutorialData, { rejectWithValue }) => {
    try {
      let response;

      if (tutorialData instanceof FormData) {
        response = await api.post(GET_TUTORIALS, tutorialData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        response = await api.post(GET_TUTORIALS, tutorialData);
      }

      return extractData(response);
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to create tutorial",
      );
    }
  },
);

export const updateTutorial = createAsyncThunk(
  "tutorials/updateTutorial",
  async ({ tutorialId, tutorialData }, { rejectWithValue }) => {
    try {
      let response;

      if (tutorialData instanceof FormData) {
        response = await api.put(`${GET_TUTORIALS}/${tutorialId}`, tutorialData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        response = await api.put(`${GET_TUTORIALS}/${tutorialId}`, tutorialData);
      }

      return extractData(response);
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update tutorial",
      );
    }
  },
);

export const deleteTutorial = createAsyncThunk(
  "tutorials/deleteTutorial",
  async (tutorialId, { rejectWithValue }) => {
    try {
      await api.delete(`${GET_TUTORIALS}/${tutorialId}`);
      return tutorialId;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete tutorial",
      );
    }
  },
);

const tutorialsSlice = createSlice({
  name: "tutorials",
  initialState,
  reducers: {
    clearTutorialDetails: (state) => {
      state.tutorialDetails = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTutorialBrowse.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTutorialBrowse.fulfilled, (state, action) => {
        state.loading = false;
        state.browse = action.payload;
      })
      .addCase(fetchTutorialBrowse.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchTutorials.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTutorials.fulfilled, (state, action) => {
        state.loading = false;
        state.tutorials = action.payload;
      })
      .addCase(fetchTutorials.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchTutorialDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTutorialDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.tutorialDetails = action.payload;
      })
      .addCase(fetchTutorialDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createTutorial.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createTutorial.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(createTutorial.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateTutorial.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateTutorial.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updateTutorial.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deleteTutorial.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteTutorial.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(deleteTutorial.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createTutorialFolder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createTutorialFolder.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(createTutorialFolder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateTutorialFolder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateTutorialFolder.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updateTutorialFolder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deleteTutorialFolder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteTutorialFolder.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(deleteTutorialFolder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearTutorialDetails } = tutorialsSlice.actions;
export default tutorialsSlice.reducer;
